/**
 * 尺寸标注命中代理（02 计划 §10.1 `SketchDimensionHitProxy` 职责，阶段 5 标签命中版）。
 *
 * 设计说明：
 * 1. 命中载体是 CSS2D 标签 DOM：标签在 DOM overlay 层吃掉指针事件，
 *    天然与 WebGL 草图几何拾取互斥（点尺寸不会触发几何拖动）；
 * 2. 通过类字段关联 dimensionId，不使用散落 `userData`；
 * 3. 单击选中、双击请求 Modify、按住拖动标注位置（超过阈值才进入拖动，
 *    mouseup 由宿主提交一笔 Model 事务）；
 * 4. 尺寸线/界线的 WebGL 命中扩展留后续阶段，本类只管标签。
 */

/** 拖动判定阈值（像素）。 */
const DRAG_THRESHOLD_PX = 3;

/** 命中代理回调。 */
export interface SketchDimensionHitProxyCallbacks {
    /** 单击选中。 */
    readonly onSelect: (dimensionId: string) => void;
    /** 双击请求修改（Modify；携带屏幕坐标供宿主定位浮层）。 */
    readonly onRequestModify: (dimensionId: string, client: {readonly x: number; readonly y: number}) => void;
    /** 拖动中（屏幕坐标，宿主换算平面局部坐标并更新预览）。 */
    readonly onDragMove: (dimensionId: string, client: {readonly x: number; readonly y: number}) => void;
    /** 拖动提交（mouseup，一笔 Model 事务）。 */
    readonly onDragCommit: (dimensionId: string, client: {readonly x: number; readonly y: number}) => void;
    /** Instant2D 值手柄拖动中（rAF 节流实时求解预览由宿主负责）；缺省时不显示手柄。 */
    readonly onValueHandleDragMove?: (dimensionId: string, client: {readonly x: number; readonly y: number}) => void;
    /** Instant2D 值手柄拖动提交（mouseup，宿主恢复 before 后走正式改值事务）。 */
    readonly onValueHandleDragCommit?: (dimensionId: string, client: {readonly x: number; readonly y: number}) => void;
    /** Instant2D 值手柄拖动取消（Esc，宿主恢复 before）。 */
    readonly onValueHandleDragCancel?: (dimensionId: string) => void;
}

/**
 * 单个尺寸标签的命中代理。
 */
export class SketchDimensionHitProxy {
    /** 标签元素。 */
    private readonly element: HTMLElement;

    /** 尺寸持久 ID。 */
    private readonly dimensionId: string;

    /** 回调集合。 */
    private readonly callbacks: SketchDimensionHitProxyCallbacks;

    /** 指针按下位置；null 表示未按下。 */
    private pressPoint: {x: number; y: number} | null = null;

    /** 是否已进入拖动。 */
    private dragging = false;

    /**
     * @param element 标签元素。
     * @param dimensionId 尺寸持久 ID。
     * @param callbacks 回调集合。
     */
    public constructor(element: HTMLElement, dimensionId: string, callbacks: SketchDimensionHitProxyCallbacks) {
        this.element = element;
        this.dimensionId = dimensionId;
        this.callbacks = callbacks;
        this.element.style.pointerEvents = 'auto';
        this.element.style.cursor = 'pointer';
        this.element.addEventListener('pointerdown', this.handlePointerDown);
        this.element.addEventListener('dblclick', this.handleDoubleClick);
        this.element.addEventListener('click', this.handleClick);
    }

    /**
     * 拦截标签自身的 click 冒泡。
     *
     * 标签是容器内的 DOM 元素，点击会一路冒泡到应用容器；容器的点击处理
     * 把"未命中实体"当作点空白并清除尺寸选中——单击标签刚选上就被清掉，
     * 表现为"拖动能进入编辑态、单击却不行"。pointerdown 的 stopPropagation
     * 拦不住独立派发的 click 事件，必须单独拦。
     */
    private readonly handleClick = (event: MouseEvent): void => {
        event.stopPropagation();
    };

    /** 指针按下：开始点击/拖动会话。 */
    private readonly handlePointerDown = (event: PointerEvent): void => {
        if (event.button !== 0) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        this.pressPoint = {x: event.clientX, y: event.clientY};
        this.dragging = false;
        window.addEventListener('pointermove', this.handlePointerMove);
        window.addEventListener('pointerup', this.handlePointerUp);
    };

    /** 指针移动：超阈值进入拖动。 */
    private readonly handlePointerMove = (event: PointerEvent): void => {
        if (!this.pressPoint) {
            return;
        }
        if (!this.dragging) {
            const moved = Math.hypot(event.clientX - this.pressPoint.x, event.clientY - this.pressPoint.y);

            if (moved < DRAG_THRESHOLD_PX) {
                return;
            }
            this.dragging = true;
        }
        this.callbacks.onDragMove(this.dimensionId, {x: event.clientX, y: event.clientY});
    };

    /** 指针抬起：拖动提交或单击选中。 */
    private readonly handlePointerUp = (event: PointerEvent): void => {
        window.removeEventListener('pointermove', this.handlePointerMove);
        window.removeEventListener('pointerup', this.handlePointerUp);
        if (!this.pressPoint) {
            return;
        }
        this.pressPoint = null;
        if (this.dragging) {
            this.dragging = false;
            this.callbacks.onDragCommit(this.dimensionId, {x: event.clientX, y: event.clientY});
            return;
        }
        this.callbacks.onSelect(this.dimensionId);
    };

    /** 双击：请求 Modify。 */
    private readonly handleDoubleClick = (event: MouseEvent): void => {
        event.stopPropagation();
        event.preventDefault();
        this.callbacks.onRequestModify(this.dimensionId, {x: event.clientX, y: event.clientY});
    };

    /** 释放全部监听。 */
    public dispose(): void {
        this.element.removeEventListener('pointerdown', this.handlePointerDown);
        this.element.removeEventListener('dblclick', this.handleDoubleClick);
        this.element.removeEventListener('click', this.handleClick);
        window.removeEventListener('pointermove', this.handlePointerMove);
        window.removeEventListener('pointerup', this.handlePointerUp);
        this.pressPoint = null;
        this.dragging = false;
    }
}
